// src/app/settings.jsx
import React, { useState, useEffect } from 'react';
import {
  View, Text, TextInput, StyleSheet, ScrollView,
  KeyboardAvoidingView, TouchableWithoutFeedback, Keyboard,
  Platform, TouchableOpacity, Modal
} from 'react-native';
import { useNavigate } from 'react-router-dom';
import { IoPeople } from 'react-icons/io5'; // Changed from IoPersonAdd to IoPeople
import axios from 'axios';
import API_BASE_URL from '../apiConfig.js';

// Helper to fetch a token from localStorage
const getToken = () => {
  try {
    return localStorage.getItem('userToken');
  } catch {
    return null;
  }
};

export default function Settings() {
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);      // live data (view mode)
  const [draft, setDraft] = useState(null);            // edit buffer
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

  const fetchUserData = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_BASE_URL}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserData(response.data || {});
      setDraft(response.data || {});
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleEdit = () => {
    setDraft(userData);     // start from current values
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const token = getToken();
      // Build payload from draft. Only include password if user typed something.
      const payload = {
        firstName: draft.firstName || '',
        lastName:  draft.lastName  || '',
        email:     draft.email     || '',
        businessName: draft.businessName || '',
        businessLogo: draft.businessLogo || '',
        address:   draft.address   || '',
        city:      draft.city      || '',
        state:     draft.state     || '',
        zip:       draft.zip       || '',
        edited: new Date().toISOString(),
      };
      if (draft.password && draft.password.trim() !== '') {
        payload.password = draft.password;
      }

      await axios.put(`${API_BASE_URL}/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setIsEditing(false);
      await fetchUserData(); // refresh with canonical values from server
    } catch (error) {
      console.error('Error saving user data:', error);
      window.alert('Failed to save settings. Please try again.');
    }
  };

  const handleChange = (name, value) => {
    setDraft((prev) => ({ ...prev, [name]: value === '' ? null : value }));
  };

  const handleDelete = async () => {
    try {
      const token = getToken();
      await axios.delete(`${API_BASE_URL}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      localStorage.removeItem('userToken');
      setDeleteModalVisible(false);
      navigate('/login');
    } catch (error) {
      console.error('Error deleting account:', error);
      window.alert('Failed to delete account. Please try again.');
    }
  };

  const handleCancel = () => {
    setDraft(userData);  // revert edits
    setIsEditing(false);
  };

  if (!userData) return <Text>Loading user data...</Text>;

  return (
    <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
        <ScrollView contentContainerStyle={{ flexGrow: 1, padding: 20 }} keyboardShouldPersistTaps="handled">
          <View style={styles.container}>
            <Text style={styles.header}>Settings</Text>
            <TouchableOpacity
              style={styles.addUserIconButton}
              onPress={() => navigate('/users')}
            >
              <div style={{
                backgroundColor: '#007BFF',
                borderRadius: '5px',
                width: 30,
                height: 30,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginRight: '8px'
              }}>
                <IoPeople size={18} color="white" />
              </div>
              <Text style={styles.usersButtonText}>Users</Text>
            </TouchableOpacity>
            <View style={styles.fieldContainer}>
              <Text style={styles.label}>First Name:</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputNoBorder]}
                value={isEditing ? (draft.firstName || '') : (userData.firstName || 'Not Set')}
                onChangeText={(t) => handleChange('firstName', t)}
                editable={isEditing}
              />
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Last Name:</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputNoBorder]}
                value={isEditing ? (draft.lastName || '') : (userData.lastName || 'Not Set')}
                onChangeText={(t) => handleChange('lastName', t)}
                editable={isEditing}
              />
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Password:</Text>
              <TouchableOpacity 
                style={styles.changePasswordButton}
                onPress={() => navigate('/change-password')}
              >
                <Text style={styles.changePasswordText}>Change Password</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Email:</Text>
              <TextInput
                style={[styles.input, styles.inputNoBorder]}
                value={userData.email || 'Not Set'}
                editable={false}
              />
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Business Name:</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputNoBorder]}
                value={isEditing ? (draft.businessName || '') : (userData.businessName || 'Not Set')}
                onChangeText={(t) => handleChange('businessName', t)}
                editable={isEditing}
              />
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Business Logo (URL):</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputNoBorder]}
                value={isEditing ? (draft.businessLogo || '') : (userData.businessLogo || 'Not Set')}
                onChangeText={(t) => handleChange('businessLogo', t)}
                editable={isEditing}
              />
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Address:</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputNoBorder]}
                value={isEditing ? (draft.address || '') : (userData.address || 'Not Set')}
                onChangeText={(t) => handleChange('address', t)}
                editable={isEditing}
              />
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>City:</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputNoBorder]}
                value={isEditing ? (draft.city || '') : (userData.city || 'Not Set')}
                onChangeText={(t) => handleChange('city', t)}
                editable={isEditing}
              />
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>State:</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputNoBorder]}
                value={isEditing ? (draft.state || '') : (userData.state || 'Not Set')}
                onChangeText={(t) => handleChange('state', t)}
                editable={isEditing}
              />
            </View>

            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Zip:</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputNoBorder]}
                value={isEditing ? (draft.zip || '') : (userData.zip || 'Not Set')}
                onChangeText={(t) => handleChange('zip', t)}
                editable={isEditing}
              />
            </View>

            <View style={styles.buttonContainer}>
              {!isEditing ? (
                <>
                  <TouchableOpacity style={[styles.button, styles.editButton]} onPress={handleEdit}>
                    <Text style={styles.buttonText}>Edit</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[styles.button, styles.deleteButton]} onPress={() => setDeleteModalVisible(true)}>
                    <Text style={styles.buttonText}>Delete Account</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <>
                  <TouchableOpacity style={[styles.button, styles.saveButton]} onPress={handleSave}>
                    <Text style={styles.saveButtonText}>Save</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[styles.button, styles.cancelButton]} onPress={handleCancel}>
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </TouchableOpacity>
                </>
              )}
            </View>
          </View>
        </ScrollView>
      </TouchableWithoutFeedback>

      <Modal visible={isDeleteModalVisible} transparent animationType="slide" onRequestClose={() => setDeleteModalVisible(false)}>
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalText}>
              Are you sure you want to delete your account? All of your saved data will be lost.
            </Text>
            <View style={styles.modalButtonContainer}>
              <TouchableOpacity style={[styles.button, { backgroundColor: 'red' }]} onPress={handleDelete}>
                <Text style={styles.buttonText}>Delete</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.button, { backgroundColor: 'gray' }]} onPress={() => setDeleteModalVisible(false)}>
                <Text style={styles.buttonText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20, paddingBottom: 100, backgroundColor: '#fff' },
  header: { fontSize: 24, fontWeight: 'bold', marginBottom: 20, textDecorationLine: 'underline' },
  fieldContainer: { marginBottom: 15 },
  label: {
    fontWeight: 'bold', marginTop: 15, fontSize: 20, color: '#333',
    borderBottomWidth: 1, borderBottomColor: '#ddd', paddingBottom: 5,
  },
  input: {
    borderWidth: 1, borderColor: '#ccc', padding: 8, borderRadius: 4, marginTop: 5, backgroundColor: '#fff', elevation: 1,
  },
  inputNoBorder: { borderWidth: 0 },
  buttonContainer: { marginTop: 30 },
  button: { flex: 1, padding: 10, borderRadius: 5, alignItems: 'center', marginHorizontal: 5 },
  buttonText: { color: 'white', fontWeight: 'bold' },
  editButton: { backgroundColor: 'blue', marginTop: 10 },
  deleteButton: {
    backgroundColor: '#FF3B30', padding: 10, marginTop: 15, borderRadius: 5, alignItems: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.2, shadowRadius: 3, elevation: 2,
  },
  saveButton: {
    backgroundColor: '#34C759', padding: 10, borderRadius: 5, alignItems: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.2, shadowRadius: 3, elevation: 2,
  },
  saveButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
  cancelButton: { backgroundColor: '#FF3B30', padding: 10, borderRadius: 8, alignItems: 'center', marginTop: 20 },
  cancelButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },

  // Modal styles
  modalContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0, 0, 0, 0.5)' },
  modalContent: { width: '80%', backgroundColor: '#fff', padding: 20, borderRadius: 10, alignItems: 'center' },
  modalText: { fontSize: 18, marginBottom: 20, textAlign: 'center' },
  modalButtonContainer: { flexDirection: 'row', justifyContent: 'space-between', width: '100%' },

  // Add User button styles
  addUserIconButton: {
    position: 'absolute', 
    padding: '15px', 
    top: 10, 
    right: 10, 
    backgroundColor: 'transparent', 
    flexDirection: 'row', 
    alignItems: 'center',
  },
  usersButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#007BFF',
  },

  // Change Password button styles
  changePasswordButton: {
    backgroundColor: '#007BFF', 
    paddingVertical: 6, 
    paddingHorizontal: 12, 
    borderRadius: 4, 
    alignItems: 'center', 
    marginTop: 5,
    alignSelf: 'flex-start'
  },
  changePasswordText: { 
    color: '#fff', 
    fontSize: 12, 
    fontWeight: 'bold' 
  },
});
